import { Link } from 'react-router-dom';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkspaceDropContext } from '@components/main-workspace/use-workspace-dnd';
import { Home, ChevronRight } from 'lucide-react';
import cn from 'classnames';
import { StyleSheet } from 'aphrodite';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { PATHS } from '../../../router/paths';
import { White } from '../../../utils/constants/style';

/**
 * Supports both shapes for easy reuse:
 * - Strict: { id, name, path? }
 * - Legacy: { label, path? }
 */
export type BreadcrumbItem =
  | { id: number | string; name: string; path?: string }
  | { label: string; path?: string };

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  currentFolderName: string;
  rootLabel?: string;
  rootPath?: string;
}

type CrumbNode =
  | { key: string; type: 'link'; name: string; to: string }
  | { key: string; type: 'current'; name: string };

const isLegacyItem = (
  item: BreadcrumbItem
): item is { label: string; path?: string } => 'label' in item;

export function Breadcrumbs({
  items,
  currentFolderName,
  rootLabel,
  rootPath
}: BreadcrumbsProps) {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();
  const getDropTargetProps = useContext(WorkspaceDropContext);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        nav: { color: getColour('normalText') },
        divider: { color: getColour('inactiveText') },
        link: {
          color: getColour('normalText'),
          textDecoration: 'none',
          ':hover': { color: getColour('activeText') }
        },
        current: {
          color: White,
          backgroundColor: getColour('activeText'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fontWeight: 600 as any
        },
        pill: {
          border: `1px solid ${getColour('border')}`,
          backgroundColor: getColour('baseLayer'),
          boxShadow: `0 1px 0 ${getColour('border')}`
        }
      }),
    [getColour]
  );

  const rootText = (rootLabel || t('home')).trim();
  const rootTo = rootPath || PATHS().HOME;
  const rootDropProps = getDropTargetProps?.(null, rootText);

  const normalized = useMemo(() => {
    const arr = items || [];

    const toName = (i: BreadcrumbItem) =>
      (isLegacyItem(i) ? i.label : i.name) ?? '';
    const toPath = (i: BreadcrumbItem) => i.path;
    const toKey = (i: BreadcrumbItem, idx: number) =>
      isLegacyItem(i)
        ? i.path
          ? `path:${i.path}`
          : `label:${i.label}:${idx}`
        : String(i.id);

    // normalize + remove blank names
    let out = arr
      .map((it, idx) => ({
        key: toKey(it, idx),
        name: String(toName(it)).trim(),
        path: toPath(it)
      }))
      .filter(it => it.name.length > 0);

    // ✅ DEDUPE: if first crumb matches root (by path when possible, else by label), drop it
    if (out.length) {
      const first = out[0];
      const samePath =
        !!first.path && !!rootTo && first.path.trim() === rootTo.trim();
      const sameLabel = first.name.toLowerCase() === rootText.toLowerCase();

      if (samePath || sameLabel) {
        out = out.slice(1);
      }
    }

    return out;
  }, [items, rootText, rootTo]);

  // Avoid duplicating "current" if last item already matches it
  const lastName = normalized.length
    ? normalized[normalized.length - 1].name
    : '';
  const currentTrimmed = (currentFolderName ?? '').trim();
  const shouldShowCurrent =
    currentTrimmed.length > 0 &&
    lastName.toLowerCase() !== currentTrimmed.toLowerCase();

  const nodes: CrumbNode[] = [
    ...normalized.map(it => ({
      key: it.key,
      type: 'link' as const,
      name: it.name,
      to: it.path ?? PATHS(it.key.toString()).FOLDER_CONTENT
    })),
    ...(shouldShowCurrent
      ? [{ key: '__current__', type: 'current' as const, name: currentTrimmed }]
      : [])
  ];

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        'inline-flex items-center flex-wrap gap-1.5 sm:gap-2',
        'text-base md:text-lg font-semibold leading-tight',
        'rounded-full p-1',
        styles.pill,
        styles.nav
      )}
    >
      {/* Root */}
      <Link
        {...rootDropProps}
        to={rootTo}
        className={cn(
          'group inline-flex items-center gap-1.5 rounded-full',
          'min-h-10 px-3 py-2',
          'max-w-[14ch] sm:max-w-[22ch] md:max-w-none truncate',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'transition-colors',
          styles.link
        )}
        style={
          {
            '--tw-ring-offset-color': getColour('baseLayer'),
            '--tw-ring-color': getColour('activeText'),
            ...rootDropProps?.style
          } as React.CSSProperties
        }
        title={rootDropProps?.title ?? rootText}
      >
        <Home
          size={14}
          className="opacity-70 group-hover:opacity-100 transition-opacity shrink-0"
        />
        <span className="truncate">{rootText}</span>
      </Link>

      {/* Nodes after root (each gets exactly one separator before it) */}
      {nodes.map(node => {
        const dropProps = Number.isFinite(Number(node.key))
          ? getDropTargetProps?.(Number(node.key), node.name)
          : undefined;
        return (
          <span
            key={node.key}
            className="inline-flex items-center gap-1.5 sm:gap-2"
          >
            <ChevronRight
              size={14}
              className={cn('opacity-80 shrink-0', styles.divider)}
            />

            {node.type === 'current' ? (
              <span
                aria-current="page"
                className={cn(
                  'inline-flex items-center font-medium text-sm text-blue-500 rounded-full px-2.5 py-0.5',
                  'max-w-[14ch] sm:max-w-[22ch] md:max-w-[30ch] truncate',
                  styles.current
                )}
                title={node.name}
              >
                {node.name}
              </span>
            ) : (
              <Link
                {...dropProps}
                to={node.to}
                className={cn(
                  'group inline-flex items-center rounded-full min-h-10 px-3 py-2',
                  'max-w-[14ch] sm:max-w-[22ch] md:max-w-[30ch] truncate',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  'transition-colors',
                  styles.link
                )}
                style={
                  {
                    '--tw-ring-offset-color': getColour('baseLayer'),
                    '--tw-ring-color': getColour('activeText'),
                    ...dropProps?.style
                  } as React.CSSProperties
                }
                title={dropProps?.title ?? node.name}
              >
                <span className="truncate">{node.name}</span>
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
